import {
  BadgeCheck,
  CalendarPlus,
  CheckCircle2,
  Cloud,
  ExternalLink,
  FileUp,
  GalleryHorizontalEnd,
  HardDrive,
  ImageIcon,
  Link2,
  LockKeyhole,
  Send,
  ShieldCheck,
  UploadCloud,
  Video,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ClientVaultEventSelector } from "../../components/ClientVaultEventSelector";
import { PageHeader } from "../../components/PageHeader";
import { getDashboardStats, getMedia, getMemoryVaultWorkspace, getStorageBilling } from "../../lib/api";
import { formatBytes, formatDate, normalizeBaseUrl, statusClass, statusLabel } from "../../lib/format";
import { useClientVaultContext, vaultQueryParams } from "../../lib/useClientVaultContext";
import type { DashboardStats, MediaItem, MemoryVaultWorkspace } from "../../lib/types";
import { useConfig } from "../../lib/useConfig";

export function MemoryMediaVaultPage() {
  const config = useConfig();
  const [vault, setVault] = useState<MemoryVaultWorkspace | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [billingUrl, setBillingUrl] = useState("");
  const [fallbackMessage, setFallbackMessage] = useState("");
  const [workspaceError, setWorkspaceError] = useState("");
  const vaultContext = useClientVaultContext();

  useEffect(() => {
    if (vaultContext.loading) return;
    if (!vaultContext.context || !vaultContext.selectedVault) {
      setVault(null);
      setStats(null);
      setMedia([]);
      return;
    }
    const load = async () => {
      setWorkspaceError("");
      const params = vaultQueryParams(vaultContext.context, vaultContext.selectedVault);
      try {
        const [vaultData, statsData, mediaData, billingData] = await Promise.all([
          getMemoryVaultWorkspace(params),
          getDashboardStats(params).catch(() => null),
          getMedia(params).catch(() => []),
          getStorageBilling().catch(() => null),
        ]);
        setVault(vaultData);
        setStats(statsData);
        setMedia(mediaData);
        setBillingUrl(billingData?.billingUrl || "");
        setFallbackMessage(billingData?.fallbackMessage || vaultData.storageSetupStatus);
      } catch (error) {
        setWorkspaceError(error instanceof Error ? error.message : "Unable to load your Memory Media Vault.");
        setVault(null);
      }
    };
    void load();
  }, [vaultContext.loading, vaultContext.selectedVaultId]);

  const baseUrl = normalizeBaseUrl(config?.publicBaseUrl || window.location.origin);
  const absoluteUrl = (value: string) => (value.startsWith("http") ? value : `${baseUrl}${value}`);
  const recentMedia = useMemo(() => media.slice(0, 5), [media]);
  const storageBytes = stats?.storageBytes ?? vault?.storageBytes ?? 0;
  const storageLimitBytes = vault?.storageLimitBytes ?? 1;
  const storagePercent = Math.min(100, Math.round((storageBytes / storageLimitBytes) * 100));
  const supportRequestUrl = config?.supportRequestUrl || `mailto:${config?.supportEmail || "support@kreatethedate.com"}`;

  if (vaultContext.loading) {
    return <div className="ivory-card rounded-lg p-10 text-center font-bold text-ktd-muted">Loading your Memory Media Vault...</div>;
  }

  if (!vault) {
    return (
      <div className="grid gap-8">
        <ClientVaultEventSelector
          context={vaultContext.context}
          selectedVault={vaultContext.selectedVault}
          selectedVaultId={vaultContext.selectedVaultId}
          loading={vaultContext.loading}
          error={vaultContext.error || workspaceError}
          onChange={vaultContext.selectVault}
        />
        <section className="ivory-card rounded-lg p-10 text-center font-bold text-ktd-muted">
          Create or assign an event vault before opening this workspace.
        </section>
      </div>
    );
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow="Premium Memory Media Vault"
        title="Your event Memory Media Vault."
        subtitle="Upload, track, and review event media before approved memories appear in your vault."
      >
        <div className="vault-rail rounded-lg border border-[#16a6a2]/35 bg-white/[0.07] p-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.2em] text-[#7ee7e2]">Connected Memory Media Vault</p>
          <p className="mt-3 text-2xl font-black text-white">{vault.connectedMemoryHubName}</p>
          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Link
              to={vault.connectedMemoryHubUrl}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-4 py-2 text-sm font-extrabold text-white"
            >
              <GalleryHorizontalEnd className="size-4" />
              Memory Media Vault
            </Link>
            <Link
              to={vault.guestUploadUrl}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-[#7ee7e2]/45 px-4 py-2 text-sm font-extrabold text-[#7ee7e2]"
            >
              <UploadCloud className="size-4" />
              Upload Files
            </Link>
          </div>
        </div>
      </PageHeader>

      <ClientVaultEventSelector
        context={vaultContext.context}
        selectedVault={vaultContext.selectedVault}
        selectedVaultId={vaultContext.selectedVaultId}
        loading={vaultContext.loading}
        error={vaultContext.error}
        onChange={vaultContext.selectVault}
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <ClientVaultStat icon={UploadCloud} label="Pending Review" value={String(stats?.pendingUploads ?? vault.pendingUploads)} />
        <ClientVaultStat icon={BadgeCheck} label="Approved Files" value={String(stats?.approvedMedia ?? vault.approvedUploads)} />
        <ClientVaultStat icon={ImageIcon} label="Photos" value={String(stats?.totalPhotos ?? 0)} />
        <ClientVaultStat icon={Video} label="Videos" value={String(stats?.totalVideos ?? 0)} />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_.95fr]">
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#087f7c]">Client Upload Area</p>
              <h2 className="mt-3 font-display text-3xl font-bold">Share memories through your event upload link.</h2>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-ktd-muted">
                Use the event-specific upload page to add photos and videos. Every submission enters pending review before
                it can appear publicly.
              </p>
            </div>
            <span className="status-pill border-[#0f9f9a]/25 bg-[#e6fbfa] text-[#075f5c]">Vault Active</span>
          </div>
          <Link
            to={vault.guestUploadUrl}
            className="focus-ring mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-5 py-3 text-sm font-extrabold text-white"
          >
            <UploadCloud className="size-4" />
            Open Upload Page
          </Link>
        </article>

        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <Link2 className="size-9 text-[#087f7c]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Guest / Family Upload Link</h2>
          <p className="mt-4 text-sm leading-6 text-ktd-muted">
            Share this link with family, guests, friends, contributors, or event helpers. They can upload photos/videos,
            add their name, and include an optional message.
          </p>
          <div className="mt-5 rounded-lg border border-[#0f9f9a]/20 bg-white p-4 text-sm font-bold text-ktd-rich">
            {absoluteUrl(vault.guestUploadUrl)}
          </div>
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => void navigator.clipboard.writeText(absoluteUrl(vault.guestUploadUrl))}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-5 py-3 text-sm font-extrabold text-white"
            >
              <Link2 className="size-4" />
              Copy Upload Link
            </button>
            <a
              href={absoluteUrl(vault.guestUploadUrl)}
              target="_blank"
              rel="noreferrer"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-[#0f9f9a]/35 px-5 py-3 text-sm font-extrabold text-[#075f5c]"
            >
              <ExternalLink className="size-4" />
              Open Link
            </a>
          </div>
        </article>
      </section>

      <section className="grid gap-6 lg:grid-cols-[.95fr_1.05fr]">
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <HardDrive className="size-9 text-[#087f7c]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Storage Usage & Setup</h2>
          <p className="mt-4 text-sm leading-6 text-ktd-muted">
            This vault uses client-owned Cloudflare storage. Availability depends on the configured D1, R2, and Turnstile
            services remaining active.
          </p>
          <div className="mt-6">
            <div className="flex items-center justify-between gap-4 text-sm font-bold text-ktd-rich">
              <span>{formatBytes(storageBytes)} used</span>
              <span>{formatBytes(storageLimitBytes)} configured limit</span>
            </div>
            <div className="mt-3 h-3 overflow-hidden rounded-full bg-white">
              <div className="h-full rounded-full bg-[#0f9f9a]" style={{ width: `${storagePercent}%` }} />
            </div>
          </div>
          {billingUrl ? (
            <a
              href={billingUrl}
              target="_blank"
              rel="noreferrer"
              className="focus-ring mt-6 inline-flex items-center gap-2 rounded-lg bg-[#0f9f9a] px-5 py-3 text-sm font-extrabold text-white"
            >
              <Cloud className="size-4" />
              Manage Cloud Storage Billing
            </a>
          ) : (
            <p className="mt-6 rounded-lg bg-[#e6fbfa] p-4 text-sm font-semibold leading-6 text-[#075f5c]">{fallbackMessage}</p>
          )}
        </article>

        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <ShieldCheck className="size-9 text-[#087f7c]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Client Instructions</h2>
          <div className="mt-5 grid gap-3">
            {vault.clientInstructions.map((instruction) => (
              <div key={instruction} className="flex items-start gap-3 rounded-lg border border-[#0f9f9a]/14 bg-white p-4">
                <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-[#0f9f9a]" />
                <p className="text-sm font-semibold leading-6 text-ktd-muted">{instruction}</p>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="grid gap-6 xl:grid-cols-[1.05fr_.95fr]">
        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#087f7c]">Upload Status Tracking</p>
              <h2 className="mt-3 font-display text-3xl font-bold">Recent Memory Media Vault files.</h2>
            </div>
            <FileUp className="size-9 text-[#087f7c]" />
          </div>
          <div className="mt-6 grid gap-3">
            {recentMedia.length ? (
              recentMedia.map((item) => (
                <div key={item.id} className="grid gap-3 rounded-lg border border-[#0f9f9a]/14 bg-white p-4 sm:grid-cols-[1fr_auto] sm:items-center">
                  <div>
                    <p className="break-words text-sm font-extrabold text-ktd-rich">{item.fileName}</p>
                    <p className="mt-1 text-xs font-semibold text-ktd-muted">
                      {item.uploaderName || "Guest"} - {formatBytes(item.fileSize)} - {formatDate(item.createdAt)}
                    </p>
                  </div>
                  <span className={`status-pill ${statusClass(item.status, item.featured)}`}>
                    {item.featured ? "Featured" : statusLabel[item.status]}
                  </span>
                </div>
              ))
            ) : (
              <p className="rounded-lg border border-[#0f9f9a]/14 bg-white p-5 text-sm font-semibold text-ktd-muted">
                New uploads will appear here after guests or clients submit files.
              </p>
            )}
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <Link
              to="/client/pending"
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-5 py-3 text-sm font-extrabold text-white"
            >
              <UploadCloud className="size-4" />
              Review Pending Uploads
            </Link>
            <Link
              to={vault.galleryUrl}
              className="focus-ring inline-flex items-center justify-center gap-2 rounded-lg border border-[#0f9f9a]/35 px-5 py-3 text-sm font-extrabold text-[#075f5c]"
            >
              <GalleryHorizontalEnd className="size-4" />
              View Approved Gallery
            </Link>
          </div>
        </article>

        <article className="ivory-card rounded-lg p-6 sm:p-8">
          <CalendarPlus className="size-9 text-[#087f7c]" />
          <h2 className="mt-5 font-display text-3xl font-bold">Request Another Event Vault</h2>
          <p className="mt-4 text-sm leading-6 text-ktd-muted">
            Contact Kreatethedate support when you want a separate vault for a future event, reunion, milestone, or
            additional Memory Media Vault project.
          </p>
          <a
            href={supportRequestUrl}
            className="focus-ring mt-5 inline-flex min-h-12 items-center justify-center gap-2 rounded-lg bg-[#0f9f9a] px-5 py-3 text-sm font-extrabold text-white"
          >
            <Send className="size-4" />
            Request Additional Vault
          </a>
        </article>
      </section>

      <section className="rounded-lg bg-[#062f31] p-6 text-white shadow-luxe sm:p-8">
        <LockKeyhole className="size-9 text-[#7ee7e2]" />
        <h2 className="mt-5 font-display text-3xl font-bold">Secure role-based access</h2>
        <p className="mt-4 max-w-4xl text-sm leading-7 text-white/72">
          This portal area is for clients and approved viewers. Guests and family only receive the shareable upload link.
          Admin approval is required before uploaded media appears publicly in the Memory Media Vault or approved gallery.
        </p>
      </section>
    </div>
  );
}

function ClientVaultStat({ icon: Icon, label, value }: { icon: LucideIcon; label: string; value: string }) {
  return (
    <article className="ivory-card rounded-lg p-5">
      <Icon className="size-7 text-[#087f7c]" />
      <p className="mt-4 text-sm font-bold text-ktd-muted">{label}</p>
      <strong className="mt-2 block font-display text-4xl text-ktd-rich">{value}</strong>
    </article>
  );
}
